import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import addMediaElementOperation from 'editor/src/store/design/operation/addMediaElementOperation';
import removeMediaElementOperation from 'editor/src/store/design/operation/removeMediaElementOperation';
import updateMediaElementByUuidOperation from 'editor/src/store/design/operation/updateMediaElementByUuidOperation';
import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { MediaImage } from 'editor/src/store/design/types';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import getResolvedAllowRotate from 'editor/src/store/gangSheet/selector/getResolvedAllowRotate';
import getResolvedGapMm from 'editor/src/store/gangSheet/selector/getResolvedGapMm';
import getResolvedMarginMm from 'editor/src/store/gangSheet/selector/getResolvedMarginMm';
import getArtworkGroups from 'editor/src/store/gangSheet/util/getArtworkGroups';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import nestOnSheet, { NestItem, NestResult } from 'editor/src/util/gangSheet/nestOnSheet';
import { elementUuids } from 'editor/src/util/uuids';

import buildNestPlan, { NestPlanGroup } from './buildNestPlan';

interface AutoNestOptions {
  // false when a caller (e.g. fillSheetWithArtworkOperation) already saved its own undo step
  saveUndoRedoStep?: boolean;
}

// Rearranges what is on the sheet: every artwork group (one artwork at one size, as
// placed on the canvas) is nested onto the sheet's content area at its current size.
// Existing elements are reused (moved, never rescaled); copies that do not fit are
// removed from the sheet. The whole nest is one undo step. Returns the nest result so
// the UI can tell the buyer about removed copies.
const autoNestOperation =
  ({ saveUndoRedoStep = true }: AutoNestOptions = {}) =>
  (dispatch: ThunkDispatch, getState: () => RootState): NestResult | undefined => {
    const state = getState();
    const gangSheetSettings = state.hostSettings.gangSheet;
    if (!gangSheetSettings) {
      return undefined;
    }

    const spreadIndex = getCurrentSpreadIndex(state);
    const focusedContent = getFocusedContent(state);
    const area = getContentAreaFromSpread(state.design.designData?.spreads[spreadIndex], focusedContent);
    if (!area) {
      return undefined;
    }

    const groups = getArtworkGroups(state.design.designData?.spreads[spreadIndex]);
    if (groups.length === 0) {
      return undefined;
    }

    const gapMm = getResolvedGapMm(state) ?? gangSheetSettings.defaultGapMm;
    const allowRotate = getResolvedAllowRotate(state) ?? gangSheetSettings.allowRotate;
    const marginMm = getResolvedMarginMm(state);

    let result: NestResult | undefined;

    batch(() => {
      if (saveUndoRedoStep) {
        dispatch(saveUndoRedoStateOperation('Auto nest'));
      }

      const items: NestItem[] = groups.map((group) => ({
        id: group.key,
        widthMm: group.widthMm,
        heightMm: group.heightMm,
        qty: group.elements.length,
      }));
      const planGroups: NestPlanGroup[] = groups.map((group) => ({
        key: group.key,
        existingUuids: group.elements.map((element) => element.uuid),
        widthMm: group.widthMm,
        heightMm: group.heightMm,
      }));

      result = nestOnSheet({ widthMm: area.width, heightMm: area.height }, items, {
        gapMm,
        allowRotate,
        marginMm,
      });

      const plan = buildNestPlan(planGroups, result.placed, area.left, area.top);

      plan.updates.forEach(({ uuid, x, y, r }) => {
        dispatch(updateMediaElementByUuidOperation(uuid, { x, y, r }));
      });

      plan.creates.forEach(({ sourceUuid, x, y, r }) => {
        const source = getMediaElementByUuid(getState(), sourceUuid) as MediaImage | undefined;
        if (source) {
          const copy: MediaImage = {
            ...source,
            uuid: elementUuids.generate(),
            x,
            y,
            r,
          };
          dispatch(addMediaElementOperation(spreadIndex, 0, copy, false));
        }
      });

      plan.removedUuids.forEach((uuid) => {
        const elementAddress = getStructureIndexByElementUuid(getState().design.designData, uuid);
        if (elementAddress) {
          dispatch(removeMediaElementOperation(elementAddress, false));
        }
      });
    });

    return result;
  };

export default autoNestOperation;
